import { useContext } from 'react';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import PropTypes from 'prop-types';
import { CURRENCY_SYMBOLS, CUSTOMER_TYPE } from 'components/common/constants';
import i18nContext from 'components/i18n-context';
import { amountFormattedValue, getErrorMessageForAlert } from 'services/utils';
import Alert from 'uikit/Alert/Alert';
import Button from 'uikit/Button/Button';
import './BetweenAccountsConfirmationTab.scss';

const BetweenAccountsConfirmationTab = ({ transferData, onConfirm, onEdit, error, isLoading, userStore }) => {
  const i18n = useContext(i18nContext);

  const dataRowContentScheme = (left, center, isTotalAmount = false) => {
    return (
      <div key={left} className={'confirmation-detail-row'}>
        <p className={'confirmation-detail-label'}>{left}</p>
        <p className={isTotalAmount ? 'confirmation-detail-total-amount-value' : 'confirmation-detail-value'}>
          {center}
        </p>
      </div>
    );
  };

  const paymentDetailsScheme = [
    {
      left: i18n.getMessage('betweenAccounts.left.amount'),
      center: `${CURRENCY_SYMBOLS[transferData.currency] || '$'} ${amountFormattedValue(transferData.amount)}`,
      isTotalAmount: false
    },
    {
      left: i18n.getMessage('betweenAccounts.left.commission'),
      center: `${CURRENCY_SYMBOLS[transferData.currency] || '$'}  ${amountFormattedValue(transferData.commission)}`,
      isTotalAmount: false
    },
    {
      left: i18n.getMessage('betweenAccounts.left.totalAmount'),
      center: `${CURRENCY_SYMBOLS[transferData.currency] || '$'}  ${amountFormattedValue(transferData.totalAmount)}`,
      isTotalAmount: true
    },
    {
      left: i18n.getMessage('betweenAccounts.left.description'),
      center: transferData.description,
      isTotalAmount: false
    }
  ];

  const recipientDetailsScheme = [
    transferData.to?.iban?.iban || transferData.to?.multi_iban?.iban
      ? {
          left: i18n.getMessage('betweenAccounts.left.recipientIban'),
          center: transferData.to?.iban?.iban || transferData.to?.multi_iban?.iban
        }
      : {
          left: i18n.getMessage('betweenAccounts.left.recipientWalletId'),
          center: transferData.to?.wallet_number
        },
    userStore?.userData?.account?.type === CUSTOMER_TYPE.COMPANY
      ? {
          left: i18n.getMessage('betweenAccounts.left.recipientCountry'),
          center: i18n.getMessage(`country.${userStore?.userData?.account?.details?.legal_address?.country}`)
        }
      : {
          left: i18n.getMessage('betweenAccounts.left.recipientCountry'),
          center: i18n.getMessage(`country.${userStore?.userData?.account?.details?.address?.country}`)
        },
    {
      left: i18n.getMessage('betweenAccounts.left.recipientName'),
      center: userStore?.userData?.account?.common_name
    }
  ];

  return (
    <div className={'between-accounts-confirmation'}>
      <div className={'confirmation-details'}>
        <div className={'confirmation-details-data-wrapper payment-details-wrapper'}>
          <p className={'confirmation-details-data-header'}>
            {i18n.getMessage('betweenAccounts.header.paymentDetails')}
          </p>
          {paymentDetailsScheme?.map(
            (rowData) => rowData && dataRowContentScheme(rowData.left, rowData.center, rowData.isTotalAmount)
          )}
        </div>

        <div className={'confirmation-details-data-wrapper'}>
          <p className={'confirmation-details-data-header'}>
            {i18n.getMessage('betweenAccounts.header.recipientDetails')}
          </p>
          {recipientDetailsScheme?.map((rowData) => rowData && dataRowContentScheme(rowData.left, rowData.center))}
        </div>
      </div>

      <Alert type={'warning'} className={'confirmation-alert'} message={getErrorMessageForAlert(i18n, error)} />

      <div className={'confirmation-actions'}>
        <Button type={'outline'} size={'large'} fullWidth={true} onClick={onEdit} isLoading={isLoading}>
          {i18n.getMessage('betweenAccounts.button.editTransfer')}
        </Button>
        <Button type={'primary'} size={'large'} fullWidth={true} onClick={onConfirm} isLoading={isLoading}>
          {i18n.getMessage('betweenAccounts.button.confirm')}
        </Button>
      </div>
    </div>
  );
};

BetweenAccountsConfirmationTab.propTypes = {
  transferData: PropTypes.shape({
    from: PropTypes.object,
    to: PropTypes.object,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    commission: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    totalAmount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    currency: PropTypes.string,
    description: PropTypes.string
  }),
  onConfirm: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  error: PropTypes.any,
  isLoading: PropTypes.bool,
  userStore: MobXPropTypes.observableObject
};

export default inject('userStore')(observer(BetweenAccountsConfirmationTab));
